'use client';

import { styled } from 'styled-components';
import { ImageProps, NonEmptyArray, SharedHeroProps, VideoProps } from '@types';
import { Icon } from '@wistia/ui';
import cs from 'classnames';
import * as Styled from '@lib/styles';
import Button from '@dvd/components/Button';
import TextLink from '@dvd/components/TextLink';
import {
  HeroMedia,
  HeroMessaging,
  HeroSubtitle,
  HeroDescription,
  HeroLogos,
  HeroComputedMedia,
} from '@dvd/modules/Heroes/HeroSharedComponents';

type HeroCheckmarkListProps = {
  /**
   * The optional eyebrow for the checkmark list
   */
  eyebrow?: string;
  /**
   * The list items for the checkmark list
   */
  listItems: NonEmptyArray<string>;
};

const HeroCheckmarkListUnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  columns: 1;

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin: 0 auto;
    column-count: 2;
    column-gap: ${({ theme }) => theme.spacing[2]};
    width: fit-content;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: 0;
  }
`;

const HeroCheckmarkListItemIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.blue500};
  min-width: auto;
`;

const HeroCheckmarkListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: ${({ theme }) => theme.spacing[0.25]};
  margin-block-end: ${({ theme }) => theme.spacing[0.5]};
  text-align: start;
`;

const HeroCheckmarkListEyebrow = styled(Styled.Eyebrow)`
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  text-align: start;

  ${({ theme }) => theme.mq.minWidth.medium} {
    text-align: center;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    text-align: start;
  }
`;

const HeroCheckmarkContent = styled.div`
  margin-block-start: ${({ theme }) => theme.spacing[3]};

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-start: ${({ theme }) => theme.spacing[4]};
  }
`;

/**
 * This component is for use specifically in the Hero to display extra content
 * in the form of an eyebrow and a list of checkmark items. The list items will be displayed
 * with a blue checkmark icon.
 */
const HeroCheckmarkList = ({ eyebrow, listItems }: HeroCheckmarkListProps) => (
  <HeroCheckmarkContent>
    {eyebrow && <HeroCheckmarkListEyebrow>{eyebrow}</HeroCheckmarkListEyebrow>}
    <HeroCheckmarkListUnorderedList>
      {listItems.map((listItem) => (
        <HeroCheckmarkListItem key={listItem}>
          <HeroCheckmarkListItemIcon type="checkmark" size="md" />
          <Styled.BodySm as="span">{listItem}</Styled.BodySm>
        </HeroCheckmarkListItem>
      ))}
    </HeroCheckmarkListUnorderedList>
  </HeroCheckmarkContent>
);

const HeroCtaContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1.5]};
  justify-content: start;

  margin-block-start: ${({ theme }) => theme.spacing[1.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    justify-content: center;
    flex-direction: row;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: start;
    justify-content: start;
  }

  // Remove the extra padding we get from the p tag
  p {
    margin-block-end: 0;
  }
`;

const HeroContainer = styled.div`
  padding-block-end: ${({ theme }) => theme.spacing[3]};
  padding-block-start: ${({ theme }) => theme.spacing[4]};
  display: grid;
  ${({ theme }) => theme.grid.columns.small}

  &.portrait {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  &.landscape {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block-end: ${({ theme }) => theme.spacing[4]};

    &.portrait {
      align-items: center;
      display: grid;
      padding-block-start: ${({ theme }) => theme.spacing[4]};
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 6;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 8 / span 5;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }

    &.landscape {
      align-items: center;
      display: grid;
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 5;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 6 / span 7;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }
  }
`;

interface HeroCheckmarksProps extends SharedHeroProps {
  /**
   * The video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  video?: VideoProps;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   * - Only `src` and `alt` properties are required; `width` and `height` are fixed for layout consistency
   */
  image?: Pick<ImageProps, 'src' | 'alt'>;
  /**
   * Extra content to display in the hero such as checkmarks or a list.
   */
  checkmarkContent: HeroCheckmarkListProps;
  /**
   * Layout variant of the hero section. Determines the layout and style.
   */
  variant?: 'portrait' | 'landscape';
  /**
   * Whether to show customer logos
   */
  showLogos?: boolean;
}

/**
 *
 * This Hero displays a checkmarks section for extra content.
 *
 */
const HeroCheckmarks = ({
  description,
  video = null,
  headline,
  image = null,
  primaryCta = null,
  secondaryCta = null,
  linkCta = null,
  showLogos = true,
  title,
  variant = 'landscape',
  checkmarkContent,
}: HeroCheckmarksProps) => {
  return (
    <HeroContainer className={cs(variant)}>
      <HeroMessaging>
        <header>
          <Styled.Eyebrow as="h1">{title}</Styled.Eyebrow>
          <HeroSubtitle aria-roledescription="subtitle">
            {headline}
          </HeroSubtitle>
        </header>
        <HeroDescription description={description} size="bodyMd" />

        {primaryCta || secondaryCta || linkCta ? (
          <HeroCtaContainer className={cs(variant)}>
            {primaryCta?.href ? (
              <Button
                color="blue500"
                href={primaryCta.href}
                trackingProperty={primaryCta.trackingProperty}
                trackingLabel={primaryCta.trackingLabel}
              >
                {primaryCta.text}
              </Button>
            ) : null}
            {secondaryCta?.href ? (
              <Button
                variant="secondary"
                href={secondaryCta.href}
                trackingProperty={secondaryCta.trackingProperty}
                trackingLabel={secondaryCta.trackingLabel}
              >
                {secondaryCta.text}
              </Button>
            ) : null}
          </HeroCtaContainer>
        ) : null}
        {/* Needs a separate HeroCtaContainer for the linkCta so it can sit outside of the HeroCtaContainer for the primary and secondary ctas */}
        {linkCta ? (
          <HeroCtaContainer className={cs(variant)}>
            <Styled.BodyLg>
              <TextLink cta={linkCta} />
            </Styled.BodyLg>
          </HeroCtaContainer>
        ) : null}
        {checkmarkContent.listItems.length > 0 ? (
          <HeroCheckmarkList
            eyebrow={checkmarkContent.eyebrow}
            listItems={checkmarkContent.listItems}
          />
        ) : null}
      </HeroMessaging>

      <HeroComputedMedia
        hashedId={video?.hashedId}
        imageAlt={image?.alt}
        imageSrc={image?.src}
        variant={variant}
        aspect={video?.aspect}
      />
      {showLogos ? <HeroLogos /> : null}
    </HeroContainer>
  );
};

export default HeroCheckmarks;
